import {
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  IconButton,
  type SelectChangeEvent,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, type ChangeEvent } from 'react';
import { licenseAPI } from '@/services/api';
import type { LicenseKey } from '@/types/domain';
import type { LogFiltersValue } from '../hooks/useLogs';

interface LogFiltersProps {
  filters: LogFiltersValue;
  onChange: (filters: LogFiltersValue) => void;
  onApply: () => void;
  onClear: () => void;
}

const ACTION_TYPES = [
  'activation',
  'heartbeat',
  'validation',
  'deactivation',
  'file_process',
];

interface LicenseKeyOption {
  key: string;
}

export default function LogFilters({
  filters,
  onChange,
  onApply,
  onClear,
}: LogFiltersProps) {
  const { t } = useTranslation();
  const [licenseKeys, setLicenseKeys] = useState<LicenseKeyOption[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);

  useEffect(() => {
    const loadLicenseKeys = async () => {
      setLoadingKeys(true);
      try {
        const response = await licenseAPI.getAll();
        const allLicenseKeys: LicenseKey[] = Array.isArray(response.data)
          ? response.data
          : [];

        const activeLicenseKeys = allLicenseKeys
          .filter((license) => license.is_active)
          .map((license) => ({ key: license.key }));

        setLicenseKeys(activeLicenseKeys);
      } catch (error) {
        console.error('Failed to load license keys:', error);
      } finally {
        setLoadingKeys(false);
      }
    };

    void loadLicenseKeys();
  }, []);

  const handleActionChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === 'all') {
      const nextFilters: LogFiltersValue = { ...filters };
      delete nextFilters.action;
      onChange(nextFilters);
      return;
    }

    onChange({
      ...filters,
      action: value,
    });
  };

  const handleStartDateChange = (date: Date | null) => {
    onChange({ ...filters, startDate: date });
  };

  const handleEndDateChange = (date: Date | null) => {
    onChange({ ...filters, endDate: date });
  };

  const handleIpAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const nextFilters: LogFiltersValue = { ...filters };
    if (value) {
      nextFilters.ipAddress = value;
    } else {
      delete nextFilters.ipAddress;
    }
    onChange(nextFilters);
  };

  const handleLicenseKeyChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === 'all') {
      const nextFilters: LogFiltersValue = { ...filters };
      delete nextFilters.licenseKey;
      onChange(nextFilters);
      return;
    }

    onChange({
      ...filters,
      licenseKey: value,
    });
  };

  const handleClearSingle = (field: keyof LogFiltersValue) => {
    if (field === 'ipAddress') {
      const nextFilters: LogFiltersValue = { ...filters };
      delete nextFilters.ipAddress;
      onChange(nextFilters);
      return;
    }

    if (field === 'licenseKey') {
      const nextFilters: LogFiltersValue = { ...filters };
      delete nextFilters.licenseKey;
      onChange(nextFilters);
      return;
    }

    if (field === 'startDate') {
      onChange({ ...filters, startDate: null });
      return;
    }

    if (field === 'endDate') {
      onChange({ ...filters, endDate: null });
      return;
    }

    const nextFilters: LogFiltersValue = { ...filters };
    delete nextFilters.action;
    onChange(nextFilters);
  };

  const hasActiveFilters =
    filters.action ||
    filters.startDate ||
    filters.endDate ||
    filters.ipAddress ||
    filters.licenseKey;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap">
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="action-filter-label">{t('logs.actionType')}</InputLabel>
            <Select
              labelId="action-filter-label"
              value={filters.action || 'all'}
              label={t('logs.actionType')}
              onChange={handleActionChange}
              size="small"
            >
              <MenuItem value="all">{t('logs.allActions')}</MenuItem>
              {ACTION_TYPES.map((action) => (
                <MenuItem key={action} value={action}>
                  {t(`logs.actions.${action}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="license-key-filter-label">{t('logs.licenseKey')}</InputLabel>
            <Select
              labelId="license-key-filter-label"
              value={filters.licenseKey || 'all'}
              label={t('logs.licenseKey')}
              onChange={handleLicenseKeyChange}
              size="small"
              disabled={loadingKeys}
            >
              <MenuItem value="all">{t('logs.allLicenseKeys')}</MenuItem>
              {licenseKeys.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label={t('logs.startDate')}
            value={filters.startDate}
            onChange={handleStartDateChange}
            {...(filters.endDate && { maxDate: filters.endDate })}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: {
                  endAdornment: filters.startDate ? (
                    <IconButton size="small" onClick={() => handleClearSingle('startDate')} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                },
              },
            }}
          />

          <DatePicker
            label={t('logs.endDate')}
            value={filters.endDate}
            onChange={handleEndDateChange}
            {...(filters.startDate && { minDate: filters.startDate })}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: {
                  endAdornment: filters.endDate ? (
                    <IconButton size="small" onClick={() => handleClearSingle('endDate')} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null,
                },
              },
            }}
          />

          <TextField
            label={t('logs.ipAddress')}
            value={filters.ipAddress || ''}
            onChange={handleIpAddressChange}
            size="small"
            sx={{ minWidth: 150 }}
            placeholder="192.168.1.1"
            InputProps={{
              endAdornment: filters.ipAddress ? (
                <IconButton size="small" onClick={() => handleClearSingle('ipAddress')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />

          <Button variant="contained" onClick={onApply} size="small">
            {t('logs.applyFilters')}
          </Button>

          {hasActiveFilters && (
            <Button variant="outlined" onClick={onClear} size="small">
              {t('logs.clearFilters')}
            </Button>
          )}
        </Stack>
      </Paper>
    </LocalizationProvider>
  );
}

// 重新导出类型以保持向后兼容
export type { LogFiltersValue } from '../hooks/useLogs';
